import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import buyerService from '../services/buyer.service';
import { BuyerResponseDto } from '../dto/response/buyer-response.dto';
import { BuyerUpdateRequestDto } from '../dto/request/buyer-update-request.dto';
import { BuyerSaveRequestDto } from '../dto/request/buyer-save-request.dto';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  buyers: [] as BuyerResponseDto[] | null,
  conveners: [] as BuyerResponseDto[] | null,
  consortiums: [] as BuyerResponseDto[] | null,
  buyer: {} as BuyerResponseDto | null,
  error: null as string | null,
};

export const fetchBuyers = createAsyncThunk('buyer/fetchBuyer', async () => {
  return await buyerService.fetchBuyers();
});

export const fetchBuyerById = createAsyncThunk(
  'buyer/fetchBuyerById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await buyerService.fetchBuyerById(params.id, params.language);
  }
);

export const updateBuyer = createAsyncThunk(
  'buyer/updateBuyer',
  async (
    params: {
      data: BuyerUpdateRequestDto;
      language: LocalesLanguagesConstants;
    },
    { rejectWithValue }
  ) => {
    try {
      return await buyerService.updateBuyer(params.data, params.language);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const fetchBuyerConvenors = createAsyncThunk(
  'buyer/fetchBuyerConvenors',
  async () => {
    return await buyerService.fetchBuyerConvenors();
  }
);

export const fetchBuyerSuperconvenors = createAsyncThunk(
  'buyer/fetchBuyerSuperconvenors',
  async () => {
    return await buyerService.fetchBuyerSuperconvenors();
  }
);

export const saveBuyer = createAsyncThunk(
  'buyer/saveBuyer',
  async (
    params: {
      data: BuyerSaveRequestDto;
      language: LocalesLanguagesConstants;
    },
    { rejectWithValue }
  ) => {
    try {
      return await buyerService.saveBuyer(params.data, params.language);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const deleteBuyer = createAsyncThunk(
  'buyer/deleteBuyer',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      await buyerService.deleteBuyer(id);
      dispatch(fetchBuyers());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const buyerSlice = createSlice({
  name: 'buyerSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuyers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBuyers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.buyers = action.payload;
      })
      .addCase(fetchBuyers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchBuyerById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBuyerById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.buyer = action.payload;
      })
      .addCase(fetchBuyerById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchBuyerConvenors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBuyerConvenors.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.conveners = action.payload;
      })
      .addCase(fetchBuyerConvenors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchBuyerSuperconvenors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBuyerSuperconvenors.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.consortiums = action.payload;
      })
      .addCase(fetchBuyerSuperconvenors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveBuyer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveBuyer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Organization added successfully.');
        state.buyer = action.payload;
      })
      .addCase(saveBuyer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Adding organization failed. ${action.payload as string}`);
      })
      .addCase(updateBuyer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateBuyer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.buyers) {
          const documentIndex = state.buyers.findIndex(
            (buyer) => buyer.id === action.payload.id
          );
          if (documentIndex !== -1) {
            state.buyers[documentIndex] = action.payload;
          }
        }
      })
      .addCase(updateBuyer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Saving organization failed. ${action.payload as string}`);
      })
      .addCase(deleteBuyer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteBuyer.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('organization deleted successfuly');
      })
      .addCase(deleteBuyer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting organization failed. ${action.payload as string}`);
      });
  },
});

export default buyerSlice.reducer;
